import { Injectable } from '@angular/core';
import {
  catchError,
  filter,
  finalize,
  map,
  switchMap, throwError
} from 'rxjs';
import { DataRangeService } from './data-range.service';
import { ErrorHandlingService } from './error-handling.service';
import { LoadingService } from './loading.service';
import { WebSocketService } from './web-socket.service';

@Injectable()
export class PictureSearchService {
  constructor(
    private webSocketService: WebSocketService,
    private loadingService: LoadingService,
    private dataService: DataRangeService,
    private errorHandlingService: ErrorHandlingService,
  ) {}

  search(params: {
    workstation?: string[];
    file?: string[];
    carline?: string[];
    ordertype?: string;
    orderident?: string[];
    originalFilename?: string[];
    startDate?: string;
    endDate?: string;
    selection?: string;
    page?: number;
    pageSize?: number;
  }) {
    const message = {
      action: 'listMetadata',
      params: params,
    };

    if (params.startDate && params.endDate) {
      this.dataService.setDates(params.startDate, params.endDate);
    }

    // Set loading to true when the job is started
    this.loadingService.isLoadingSubject$.next(true);

    // Send the message to start the job
    this.webSocketService.sendMessage(message);

    // Capture and track jobId when the 'started' message is received
    return this.webSocketService.getMessages().pipe(
      // Filter only messages that signal the job has started
      filter((msg) => 
        (msg.status === 'started' && msg.jobId) || 
        Number(msg.statusCode) >= 400),

      // Switch to listen for the 'completed' message with the same jobId
      switchMap((msg) => {
        const statusCode = Number(msg.statusCode);
        //error before job started
        if (statusCode >= 400) {
          let errorMessage = 'Error from Server';
          if (msg.body) {
            try {
              const body = typeof msg.body === 'string' ? JSON.parse(msg.body) : msg.body;
              errorMessage = body.message || errorMessage;
            } catch {
              errorMessage = msg.body;
            }
          } else if (msg.message) {
            errorMessage = msg.message;
          }
          const error: any = new Error(errorMessage);
          error.status = msg.statusCode;
          return throwError(() => error);
        }
        
        const jobId = msg.jobId;

          return this.webSocketService.getMessages().pipe(
            filter((msg) => (msg.status === 'completed' || Number(msg.statusCode) >= 400) && msg.jobId === jobId),
            map((msg) => {
              if (msg.status === 'completed') {
                return msg.metadata; // Return the metadata
              }

              const statusCode = Number(msg.statusCode);
              let errorMessage = 'Error from Server';

              //in the current response, the error message is in msg.body.message
              if (msg.body) {
                try {
                  const body = typeof msg.body === 'string' ? JSON.parse(msg.body) : msg.body;
                  errorMessage = body.message || errorMessage;
                } catch {
                  errorMessage = msg.body;
                }
              } else if (msg.message) {
                errorMessage = msg.message;
              }
              const error: any = new Error(errorMessage);
              error.status = statusCode;
              
              throw error;
            }),
          );
        }),

        //it must be on the exterior flow
        catchError((error) => {
          return throwError(() => error); // Re-throw the error to propagate
        }),
          // Ensure loading indicator is turned off after job completion or error
          finalize(() => {
            this.loadingService.isLoadingSubject$.next(false); // Stop loading after job completion
          }),
        );
  }
}
